import type { TimesheetEntryListItem } from './types';

export interface DayGroup {
  key: string;
  date: string;
  entries: TimesheetEntryListItem[];
}

/**
 * Agrupa lançamentos por `data` (YYYY-MM-DD) e ordena os grupos do mais
 * recente para o mais antigo (desc). A ordem dos lançamentos dentro de cada
 * grupo preserva a ordem de entrada.
 */
export function groupByDay(rows: readonly TimesheetEntryListItem[]): DayGroup[] {
  const map = new Map<string, TimesheetEntryListItem[]>();
  for (const r of rows) {
    const arr = map.get(r.data);
    if (arr) arr.push(r);
    else map.set(r.data, [r]);
  }
  return Array.from(map.entries())
    .sort(([a], [b]) => (a < b ? 1 : a > b ? -1 : 0))
    .map(([date, entries]) => ({ key: date, date, entries }));
}

/**
 * Soma `horas` em formato HH:MM e devolve o total como HH:MM (sem teto — pode
 * ultrapassar 24h quando houver múltiplos lançamentos no mesmo dia).
 */
export function sumHoras(entries: readonly TimesheetEntryListItem[]): string {
  let total = 0;
  for (const e of entries) {
    const [h = 0, m = 0] = (e.horas || '00:00').split(':').map((n) => Number.parseInt(n, 10) || 0);
    total += h * 60 + m;
  }
  const hh = Math.floor(total / 60)
    .toString()
    .padStart(2, '0');
  const mm = (total % 60).toString().padStart(2, '0');
  return `${hh}:${mm}`;
}

/**
 * Tempo efetivo no mesmo critério do GeneXus:
 * `getColaboradorApontamentoTotalHorasCalculada` (horas × multiplicador,
 * arredondando segundo fracionário para cima) e depois
 * `getHoraParaDecimalHora` (hora + minuto / 60, 2 casas).
 */
export function tempoEfetivoDecimal(
  horas: string | null | undefined,
  multiplicador: number | null | undefined,
): number | null {
  if (!horas || multiplicador == null || !Number.isFinite(multiplicador) || multiplicador === 0) {
    return null;
  }
  const match = /^(\d+):(\d{2})$/.exec(horas.trim());
  if (!match) return null;
  const segundos = (Number(match[1]) * 3600 + Number(match[2]) * 60) * multiplicador;
  const inteiro = Math.trunc(segundos);
  const totalSegundos =
    segundos - inteiro > 0 ? (Math.trunc(inteiro / 60) + 1) * 60 : inteiro;
  const horasInteiras = Math.floor(totalSegundos / 3600);
  const minutos = Math.floor((totalSegundos % 3600) / 60);
  return Math.round(((horasInteiras * 3600 + minutos * 60) / 3600) * 100) / 100;
}
